import { type HeadersFunction, json, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import React from 'react';

import BrandDetail from '~/sections/brand-page/BrandDetail';
import { Brand } from '~/types/Brand';
import { Product } from '~/types/Product';
import { apiFetch } from '~/utils/apiFetch';
import { publicPageHeaders } from '~/utils/cacheControl.server';
import { buildPageMeta } from '~/utils/meta';

export interface BrandLoaderData {
  brand: Brand;
  products: Product[];
  numberOfPages: number;
  hasMore: boolean;
  totalProductsCount: number;
  API_URL: string;
}

const PER_PAGE = 20;

export async function loader({ params, request }: LoaderFunctionArgs): Promise<Response> {
  const API_URL = process.env.PUBLIC_API_URL;
  const slug = params.slug;

  if (!slug) {
    throw new Response('Brand slug missing', { status: 400 });
  }

  const url = new URL(request.url);
  const page = Number(url.searchParams.get('page')) || 1;

  const brandRes = await apiFetch(`${API_URL}/api/v2/shop/brands/${slug}`, {}, { request });

  if (!brandRes.ok) {
    throw new Response('Brand not found', { status: 404 });
  }

  const brand: Brand = await brandRes.json();

  const productsRes = await apiFetch(
    `${API_URL}/api/v2/shop/products/search?limit=${PER_PAGE}&page=${page}&facets[brand][]=${slug}&imageFilter=sylius_shop_product_thumbnail`,
    {},
    { request }
  );
  const productsData = await productsRes.json();

  return json<BrandLoaderData>({
    brand,
    products: productsData.products || [],
    numberOfPages: productsData.pagination?.total_pages || 1,
    hasMore: productsData.pagination?.has_next_page || false,
    totalProductsCount: productsData.pagination?.total_items || 0,
    API_URL: API_URL || '',
  });
}

export const headers: HeadersFunction = ({ parentHeaders }) => publicPageHeaders(parentHeaders);

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  return buildPageMeta(data?.brand?.name || 'Značka', matches);
};

const BrandPageRoute: React.FC = () => {
  const data = useLoaderData<typeof loader>();
  return <BrandDetail serverData={data as BrandLoaderData} />;
};

export default BrandPageRoute;
